import {Rng,hashKey} from '../../shared';
import type {HeroBuild} from '../../shared/equipment';
import {BIOMES,CHOICES,ENCOUNTERS,EPITHETS,QUESTS,WEATHER,WORLD_SIZE,type Encounter,type ExpeditionChoice} from '../../content/explore/world';
import {RELICS} from '../../content/explore/events';
import type {Journey} from './index';
export interface Place {id:number;seed:number;biome:number;weather:number;epithet:number;encounter:Encounter;danger:number}
export interface Trek {
 v:1;worldSeed:string;node:number;phase:'path'|'encounter'|'fight';visited:number[];
 quest:typeof QUESTS[number];questProgress:number;favour:number;trouble:number;flags:string[];
 encounter:Encounter;fight:null|{hp:number;maxHp:number;turn:number;intent:'strike'|'guard'|'feint'};
 build?:HeroBuild;last?:{key:string;food:number;wounds:number;finds:number;quest:boolean};
}
export interface Exit {id:number;place:Place;quest:boolean;cost:number}
export function placeAt(seed:string,id:number,crypt=false):Place {
 const node=((Math.trunc(id)-1)%WORLD_SIZE+WORLD_SIZE)%WORLD_SIZE+1,r=new Rng(`${seed}:place:${node}`);
 return {id:node,seed:hashKey(seed,node),biome:crypt?11:r.int(0,10),weather:r.int(0,3),epithet:r.int(0,EPITHETS.length-1),encounter:r.pick(ENCOUNTERS),danger:r.int(0,2)};
}
export function newExpedition(seed:string,index:number,mode:'road'|'crypt',careful:boolean,build?:HeroBuild,mothDebt=false):Journey {
 const id=`${seed}:voyage:${index}`,r=new Rng(id),node=r.int(1,WORLD_SIZE);
 return {id,seed:id,step:0,supply:7,wounds:0,loot:0,careful,mode,events:[],status:'active',history:[],claimed:false,relic:null,
  trek:{v:1,worldSeed:seed,node,phase:'path',visited:[node],quest:r.pick(QUESTS),questProgress:0,favour:0,trouble:0,flags:mothDebt?['moth-debt']:[],encounter:'camp',fight:null,...(build?{build:structuredClone(build)}:{})}};
}
export function expeditionLength(j:Journey):number{return j.mode==='crypt'?10:8;}
export function exits(j:Journey):Exit[]{
 const t=j.trek;if(!t)return [];
 const r=new Rng(j.seed).stream('exits',j.step,t.node),questRoute=r.int(0,2);
 const marked=j.step>=1&&(j.step%2===1||t.flags.includes('map'));
 // Keep the original draws and quest slot. Collision repair uses addresses,
 // not extra draws that would also change the following route proposals.
 const routes:Exit[]=[0,1,2].map(i=>{
  const id=(t.node+[1,997,7919][i]+r.int(0,331))%WORLD_SIZE+1,quest=t.questProgress<2&&i===questRoute&&marked;
  const place=placeAt(t.worldSeed,id,j.mode==='crypt');
  if(quest)place.encounter='quest';
  return {id:i,place,quest,cost:t.flags.includes('bridge')?0:1};
 });
 const usedNodes=new Set([t.node]),usedEvents=new Set<Encounter>();
 const reserve=(route:Exit)=>{usedNodes.add(route.place.id);usedEvents.add(route.place.encounter);};
 const quest=routes.find(route=>route.quest);if(quest)reserve(quest);
 const pickPlace=(start:number,wanted?:Encounter):Place=>{
  // Probe each real address at most once. No relabelling: the offered event,
  // terrain, weather and danger must all belong to the node entered by travel.
  for(let offset=0;offset<WORLD_SIZE;offset++){
   const place=placeAt(t.worldSeed,(start-1+offset)%WORLD_SIZE+1,j.mode==='crypt');
   if(!usedNodes.has(place.id)&&!usedEvents.has(place.encounter)&&(!wanted||place.encounter===wanted))return place;
  }
  throw new Error('Exploration world has no distinct route destination');
 };
 // A supply camp stays available every third junction, even when slot zero
 // is reserved for the quest. Reserve it before selecting ordinary events.
 const camp=j.step%3===2?routes.find(route=>!route.quest):undefined;
 if(camp){camp.place=pickPlace(camp.place.id,'camp');reserve(camp);}
 for(const route of routes){
  if(route===quest||route===camp)continue;
  route.place=pickPlace(route.place.id);reserve(route);
 }
 return routes;
}
const clone=(j:Journey)=>structuredClone(j);
function finishStep(j:Journey):void {
 const t=j.trek!;j.step++;t.phase='path';t.fight=null;
 if(j.wounds>=5){j.status='failed';j.loot=Math.floor(j.loot/3);}
 else if(j.step>=expeditionLength(j)){j.status='complete';j.relic=new Rng(j.seed).stream('relic',t.questProgress).pick(RELICS);}
}
export function travel(journey:Journey,exitId:number):Journey {
 if(journey.status!=='active'||journey.trek?.phase!=='path')return journey;
 const exit=exits(journey).find(e=>e.id===exitId);if(!exit)return journey;
 const j=clone(journey),t=j.trek!,before=j.supply;
 if(j.supply<exit.cost)j.wounds++;else j.supply-=exit.cost;
 t.flags=t.flags.filter(f=>f!=='bridge');t.node=exit.place.id;t.encounter=exit.place.encounter;t.phase='encounter';
 if(!t.visited.includes(t.node))t.visited.push(t.node);
 t.last={key:before<exit.cost?'hunger':'arrive',food:j.supply-before,wounds:j.wounds-journey.wounds,finds:0,quest:false};
 if(j.wounds>=5){j.status='failed';j.loot=Math.floor(j.loot/3);}
 return j;
}
export function expeditionRisk(j:Journey,c:ExpeditionChoice):number {
 if(!c.risk)return 0;
 const t=j.trek!,p=placeAt(t.worldSeed,t.node,j.mode==='crypt');
 return Math.max(5,Math.min(90,c.risk+(j.mode==='crypt'?8:0)+p.danger*5+t.trouble*4-t.favour*2-(j.careful?10:0)-(t.flags.includes('companion')?10:0)-(t.flags.includes('map')?5:0)));
}
export function expeditionChoices(j:Journey):readonly ExpeditionChoice[]{
 const t=j.trek;if(!t)return [];
 if(t.encounter==='beast'&&t.flags.includes('moth-debt'))return [{id:'invoke',food:0,finds:0,heal:0,risk:0,hurt:0},...CHOICES.beast];
 if(t.encounter!=='quest')return CHOICES[t.encounter];
 const [follow,brave,leave]=CHOICES.quest;
 // The same persisted two-stage quest DTO supports different preparations.
 // A map replaces surveying work; goodwill waives the courier's toll; rain
 // makes exposed lamp-lighting riskier. All costs are visible before choosing.
 if(t.quest==='courier')return t.questProgress===0
  ?[{...follow,flag:'map'},{...brave,risk:20,trouble:1},leave]
  :[{...follow,food:0,finds:t.favour>0?2:-2},{...brave,risk:35,trouble:1},leave];
 if(t.quest==='lantern')return t.questProgress===0
  ?[{...follow,food:0,finds:-2},{...brave,food:-1,finds:2,risk:40},leave]
  :[{...follow,food:-2,finds:2},{...brave,risk:placeAt(t.worldSeed,t.node,j.mode==='crypt').weather===1?50:30},
    ...(!t.flags.includes('moth-debt')?[{id:'free',food:0,finds:0,heal:0,risk:0,hurt:0,quest:true}]:[]),leave];
 return t.questProgress===0
  ?[{...follow,food:t.flags.includes('map')?0:-2,flag:'map'},{...brave,risk:35},leave]
  :[{...follow,food:t.flags.includes('companion')?-1:-2,heal:1,favour:1},{...brave,risk:40},leave];
}
export function canChoose(j:Journey,c:ExpeditionChoice):boolean{return j.supply+c.food>=0&&j.loot+c.finds>=0;}
export function resolveEncounter(journey:Journey,id:string):Journey {
 if(journey.status!=='active'||journey.trek?.phase!=='encounter')return journey;
 const c=expeditionChoices(journey).find(c=>c.id===id);if(!c||!canChoose(journey,c))return journey;
 const j=clone(journey),t=j.trek!;
 if(id==='invoke'){
  // The debt changes the encounter, not its reward. It cannot also pay a beast
  // victory, finish a step twice, or be invoked again after a reload.
  t.flags=t.flags.filter(f=>f!=='moth-debt');if(!t.flags.includes('moth-paid'))t.flags.push('moth-paid');
  t.encounter='traveller';t.last={key:'mothPaid',food:0,wounds:0,finds:0,quest:false};
  j.history.push({event:'beast',choice:'voyage.invoke',hurt:false,loot:0,supplyDelta:0,woundDelta:0});return j;
 }
 if(c.fight){const hp=10+placeAt(t.worldSeed,t.node).danger*2;t.phase='fight';t.fight={hp,maxHp:hp,turn:0,intent:new Rng(j.seed).stream('foe',j.step,0).pick(['strike','guard','feint'])};return j;}
 const hurt=new Rng(j.seed).stream('decision',j.step,t.node,id).chance(expeditionRisk(j,c)/100);
 j.supply=Math.min(9,j.supply+c.food);j.wounds=Math.min(5,Math.max(0,j.wounds-c.heal)+(hurt?c.hurt:0));
 const gain=c.finds<0?c.finds:hurt?Math.floor(c.finds/2):c.finds+(!j.careful&&c.finds>0?1:0);j.loot=Math.max(0,j.loot+gain);
 t.favour=Math.max(0,Math.min(5,t.favour+(c.favour??0)));t.trouble=Math.max(0,Math.min(5,t.trouble+(c.trouble??0)));
 if(c.flag&&!t.flags.includes(c.flag))t.flags.push(c.flag);
 const quest=!!c.quest&&!hurt&&t.questProgress<2;
 if(quest){t.questProgress++;if(t.questProgress===2){
  if(id==='free'){for(const flag of ['moth-debt','moth-freed'])if(!t.flags.includes(flag))t.flags.push(flag);}
  else{j.loot+=6;t.favour++;if(!t.flags.includes('companion'))t.flags.push('companion');}
 }}
 t.last={key:quest?(t.questProgress===2?id==='free'?'mothFreed':'questDone':'clue'):hurt?'hurt':c.flag??(c.trouble?'trouble':'safe'),food:j.supply-journey.supply,wounds:j.wounds-journey.wounds,finds:j.loot-journey.loot,quest};
 j.history.push({event:t.encounter,choice:`voyage.${id}`,hurt,loot:j.loot-journey.loot,supplyDelta:t.last.food,woundDelta:t.last.wounds});finishStep(j);return j;
}
export function expeditionStrike(journey:Journey,action:'strike'|'guard'|'feint'|'flee'):Journey {
 if(journey.status!=='active'||journey.trek?.phase!=='fight'||!journey.trek.fight)return journey;
 const j=clone(journey),t=j.trek!,fight=t.fight!;
 if(action==='flee'){j.wounds++;t.last={key:'flee',food:0,wounds:1,finds:0,quest:false};j.history.push({event:'beast',choice:'voyage.flee',hurt:true,loot:0,supplyDelta:0,woundDelta:1});finishStep(j);return j;}
 const counter=action==='guard'&&fight.intent==='strike'||action==='feint'&&fight.intent==='guard'||action==='strike'&&fight.intent==='feint';
 const power=Math.min(2,Math.floor((t.build?.stats.attack??16)/12));
 const damage=counter?4+power:action==='strike'?2+power:1;
 fight.hp=Math.max(0,fight.hp-damage);
 const wounded=fight.hp>0&&!counter&&(action!=='guard'||fight.intent==='feint');if(wounded)j.wounds++;
 t.last={key:counter?'counter':wounded?'clash':'brace',food:0,wounds:wounded?1:0,finds:0,quest:false};
 fight.turn++;fight.intent=new Rng(j.seed).stream('foe',j.step,fight.turn).pick(['strike','guard','feint']);
 if(fight.hp===0){j.loot+=5;t.trouble=Math.max(0,t.trouble-1);t.last={key:'victory',food:0,wounds:0,finds:5,quest:false};j.history.push({event:'beast',choice:'voyage.fight',hurt:false,loot:5,supplyDelta:0,woundDelta:0});finishStep(j);}
 else if(j.wounds>=5){j.status='failed';j.loot=Math.floor(j.loot/3);}
 return j;
}
export function expeditionRetreat(journey:Journey):Journey {
 if(journey.status!=='active'||!journey.trek||journey.step<1||journey.trek.phase==='fight')return journey;
 return {...clone(journey),status:'retreated'};
}
